import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';

import { AccessType } from '@/types/property.types';

import {
  useAccessFormValidation,
  type AccessFormValues
} from './useAccessFormValidation';
import type { ServicePlan } from '../components/OrderSummarySections';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Loose input type for both wizard (SelectedServiceData) and cart-slider
 * (PropertyCartItem) callers. The hook only reads the price-ish fields and
 * specialInstructions to seed initial form state.
 */
interface ServiceData {
  totalPrice?: number;
  selectedPrice?: number;
  price?: number;
  specialInstructions?: string | null;
}

interface OrderSummaryInfoData {
  accessType?: string | null;
  accessCode?: string | null;
  lockboxLocation?: string | null;
  rentlyRegistrationPin?: string | null;
  onsiteContactName?: string | null;
  onsiteContactPhone?: string | null;
  specialInstructions?: string | null;
  firstAvailableDateStatus?: string | null;
  firstAvailableDate?: string | null;
}

/**
 * Validated form output. Returned by buildResultData() once validate()
 * passes. Each consumer (wizard / cart) wires this into its own emit shape.
 *
 * The trailing index signature lets it flow into PlaceOrderPage's
 * setOrderSummaryInfo prop, which is typed Record<string, unknown> upstream
 * (usePlaceOrder uses loose state types). Tightening that upstream is its
 * own cleanup; the index signature here doesn't hide anything since all
 * declared fields are assignable to unknown anyway.
 */
export interface OrderSummaryFormResult {
  firstAvailableDate: string | null;
  firstAvailableDateStatus: string;
  isOnsiteAppointment: boolean;
  isAccessNeeded: boolean;
  accessType: string | null;
  accessCode: string | null;
  lockboxLocation: string | null;
  rentlyRegistrationPin: string | null;
  onsiteContactName: string | null;
  onsiteContactPhone: string | null;
  specialInstructions: string | null;
  [key: string]: unknown;
}

interface UseOrderSummaryFormArgs {
  data?: ServiceData;
  orderSummaryInfo?: OrderSummaryInfoData | null;
  isCartOrder: boolean;
  isCartOrderFirstAvailableDate?: string | null;
  isCartOrderFirstAvailableDateStatus?: string;
  services?: { services?: ServicePlan[] };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOrderSummaryForm({
  data,
  orderSummaryInfo,
  isCartOrder,
  isCartOrderFirstAvailableDate,
  isCartOrderFirstAvailableDateStatus,
  services,
}: UseOrderSummaryFormArgs) {
  // -- Form state ----------------------------------------------------------
  const [accessType, setAccessType] = useState<string | null>(null);
  const [contactName, setContactName] = useState<string | null>(null);
  const [contactNumber, setContactNumber] = useState<string | null>(null);
  const [additionalNotes, setAdditionalNotes] = useState<string | undefined>();
  const [lockboxLocation, setLockboxLocation] = useState<string | null>(null);
  const [rentlyRegistrationPin, setRentlyRegistrationPin] = useState<
    string | null
  >(null);
  const [accessCode, setAccessCode] = useState<string | null>(null);
  const [firstAvailableDateStatus, setFirstAvailableDateStatus] =
    useState('Yes');
  const [firstAvailableDate, setFirstAvailableDate] = useState<string | null>(
    null
  );
  const [accessInfoTotal, setAccessInfoTotal] = useState(0);

  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [totalPlans, setTotalPlans] = useState(0);

  const { errors, validate, clearFieldError } = useAccessFormValidation();

  // -- Initialise from saved info -----------------------------------------
  useEffect(() => {
    if (data?.totalPrice != null && data.totalPrice >= 0) {
      setAccessInfoTotal(data.totalPrice);
    } else if (data?.selectedPrice != null && data.selectedPrice >= 0) {
      setAccessInfoTotal(data.selectedPrice);
    } else if (data?.price != null) {
      setAccessInfoTotal(data.price);
    }
    if (orderSummaryInfo?.accessType) {
      setAccessType(orderSummaryInfo.accessType);
    }
    if (isCartOrder) setAdditionalNotes(data?.specialInstructions ?? undefined);
    if (orderSummaryInfo) {
      if (!isCartOrder) setAdditionalNotes(orderSummaryInfo.specialInstructions ?? undefined);
      setAccessCode(orderSummaryInfo.accessCode ?? null);
      setContactName(orderSummaryInfo.onsiteContactName ?? null);
      setContactNumber(orderSummaryInfo.onsiteContactPhone ?? null);
      setLockboxLocation(orderSummaryInfo.lockboxLocation ?? null);
      setRentlyRegistrationPin(orderSummaryInfo.rentlyRegistrationPin ?? null);
      if (isCartOrder) {
        setFirstAvailableDateStatus(isCartOrderFirstAvailableDateStatus ?? 'Yes');
      } else if (orderSummaryInfo.firstAvailableDateStatus) {
        setFirstAvailableDateStatus(orderSummaryInfo.firstAvailableDateStatus);
      }
      const dateSource = isCartOrder
        ? isCartOrderFirstAvailableDate
        : orderSummaryInfo.firstAvailableDate;
      if (dateSource) setFirstAvailableDate(dayjs(dateSource).format('MMMM D, YYYY'));
    }
    // Re-seed form when orderSummaryInfo changes; isCartOrder*/setters are
    // stable — listing them would refire without semantic value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSummaryInfo]);

  useEffect(() => {
    if (services?.services && services.services.length > 0) {
      setPlans(services.services);
      setTotalPlans(services.services.length);
    }
    // Mount-only seed from initial services prop; subsequent service edits
    // do not flow back into local plans state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- Helpers -------------------------------------------------------------
  const collectFormValues = useCallback((): AccessFormValues => ({
    accessType, accessCode, lockboxLocation, rentlyRegistrationPin,
    contactName, contactNumber, additionalNotes,
    firstAvailableDateStatus, firstAvailableDate
  }), [
    accessType, accessCode, lockboxLocation, rentlyRegistrationPin,
    contactName, contactNumber, additionalNotes,
    firstAvailableDateStatus, firstAvailableDate
  ]);

  const handleAccessFieldChange = useCallback(
    (field: string, value: string | null) => {
      clearFieldError(field);
      switch (field) {
        case 'accessType': setAccessType(value); break;
        case 'lockboxLocation': setLockboxLocation(value); break;
        case 'accessCode': setAccessCode(value); break;
        case 'rentlyRegistrationPin': setRentlyRegistrationPin(value); break;
        case 'contactName': setContactName(value); break;
        case 'contactNumber': setContactNumber(value); break;
        case 'additionalNotes': setAdditionalNotes(value ?? undefined); break;
      }
    },
    [clearFieldError]
  );

  // Validate the form. Returns true if valid; surfaces field errors otherwise.
  const validateForm = useCallback(
    () => validate(collectFormValues()),
    [validate, collectFormValues]
  );

  // Build the validated form result. Caller is responsible for validating
  // first (typically via tryBuildResult below).
  //
  // The picked date is the source of truth on submit (DV-445). The radio is
  // a UI affordance; if firstAvailableDate is set, the user wants that date
  // even if the radio drifted to "Yes" (e.g. accidental click, stale state
  // on remount). The status flag is rederived so caller payloads match.
  const buildResultData = useCallback((): OrderSummaryFormResult => {
    const isAccessNeeded =
      accessType === AccessType.LOCKBOX || accessType === AccessType.KEYPAD;
    const submittedDate =
      firstAvailableDate && dayjs(firstAvailableDate).isValid()
        ? dayjs(firstAvailableDate).format('YYYY-MM-DD')
        : null;
    return {
      firstAvailableDate: submittedDate,
      firstAvailableDateStatus: submittedDate ? 'No' : 'Yes',
      isOnsiteAppointment: !!(contactName || contactNumber),
      onsiteContactName: contactName,
      onsiteContactPhone: contactNumber?.replace(/\D/g, '') ?? null,
      isAccessNeeded,
      accessType,
      lockboxLocation: accessType === AccessType.LOCKBOX ? lockboxLocation : null,
      specialInstructions: additionalNotes ?? null,
      accessCode:
        accessType === AccessType.LOCKBOX || accessType === AccessType.KEYPAD
          ? accessCode : null,
      rentlyRegistrationPin:
        accessType === AccessType.RENTLY ? rentlyRegistrationPin : null
    };
  }, [
    accessType, accessCode, lockboxLocation, rentlyRegistrationPin,
    contactName, contactNumber, additionalNotes,
    firstAvailableDate
  ]);

  // Validate and build in one call. Returns the result on success, null on
  // validation failure (errors will be surfaced via the errors state).
  const tryBuildResult = useCallback((): OrderSummaryFormResult | null => {
    if (!validateForm()) return null;
    return buildResultData();
  }, [validateForm, buildResultData]);

  // Keep the radio and the date input in sync. Toggling either way clears
  // any previously picked date so the two never drift (DV-445):
  //   - "Yes, head over ASAP" never silently carries a stale date
  //   - "No, schedule on..." starts the user on a fresh date pick
  const handleStatusChange = (s: string) => {
    clearFieldError('firstAvailableDate');
    setFirstAvailableDateStatus(s);
    setFirstAvailableDate(null);
  };

  const handleDateChange = (d: string | null) => {
    clearFieldError('firstAvailableDate');
    setFirstAvailableDate(d);
  };

  const defaultNotes = isCartOrder
    ? data?.specialInstructions ?? undefined
    : orderSummaryInfo?.specialInstructions ?? undefined;

  return {
    // form field values
    accessType,
    lockboxLocation, accessCode, rentlyRegistrationPin,
    contactName, contactNumber, additionalNotes,
    firstAvailableDateStatus, firstAvailableDate,
    accessInfoTotal,
    plans, totalPlans,
    // validation + builders (callers wire their own flow)
    errors,
    validate: validateForm,
    buildResultData,
    tryBuildResult,
    // handlers
    handleAccessFieldChange,
    handleStatusChange, handleDateChange,
    defaultNotes
  };
}
